import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useManifest } from '@/lib/queries';
import { useUI } from '@/lib/store';
import { MemoryMap } from '@/components/map/MemoryMap';
import { PinSheet } from '@/components/pin/PinSheet';
import { ComposeSheet } from '@/components/compose/ComposeSheet';
import { ComposeNote } from '@/components/notes/ComposeNote';
import { NotesStrip } from '@/components/notes/NotesStrip';
import { HistorySheet } from '@/components/notes/HistorySheet';
import { EmptyMapInvitation } from '@/components/empty/EmptyMapInvitation';
import { BucketSheet } from '@/components/bucket/BucketSheet';
import { NetStatus } from '@/components/network/NetStatus';
import { Heart } from '@/components/ui/Heart';

export const Route = createFileRoute('/')({ component: Home });

function Home() {
  const { data, isLoading, isSyncing } = useManifest();
  const selectedPinId = useUI((s) => s.selectedPinId);
  const composing = useUI((s) => s.composing);
  const startCompose = useUI((s) => s.startCompose);
  const openHistory = useUI((s) => s.openHistory);
  const openHelp = useUI((s) => s.openHelp);
  const openBucket = useUI((s) => s.openBucket);
  const selected = data?.pins.find((p) => p.id === selectedPinId) ?? null;
  const keptCount = data?.kept?.length ?? 0;
  const pinCount = data?.pins.length ?? 0;
  const wishCount = (data?.bucket_list ?? []).filter((b) => !b.completed).length;
  const showEmpty = !!data && !isLoading && pinCount === 0;
  const [emptyDismissed, setEmptyDismissed] = useState(false);
  const placeFabRef = useRef<HTMLButtonElement | null>(null);

  return (
    <main className="home">
      <MemoryMap pins={data?.pins ?? []} loading={isLoading} />

      <header className="home__header">
        <div className="home__brand">
          <Heart size={22} />
          <span className="home__brand-text">our places</span>
          <NetStatus syncing={isSyncing} />
        </div>
        <div className="home__pills">
          {wishCount > 0 && <BucketPill count={wishCount} onClick={openBucket} />}
          {keptCount > 0 && <KeptPill count={keptCount} onClick={openHistory} />}
          <CountBadge count={data?.pins.length ?? 0} />
          {pinCount > 0 && <HelpPill onClick={openHelp} />}
        </div>
      </header>

      {data && <NotesStrip notes={data.notes ?? []} members={data.members ?? []} />}

      <AnimatePresence>
        {showEmpty && !emptyDismissed && (
          <EmptyMapInvitation
            key="empty"
            targetRef={placeFabRef}
            onDismiss={() => setEmptyDismissed(true)}
          />
        )}
      </AnimatePresence>

      <div className="home__fabstack">
        {/* Note FAB — smaller, pastel, paper-fold icon. Sits above the place FAB
            so the dominant action stays the place pin. */}
        <motion.button
          whileTap={{ scale: 0.92, rotate: -4 }}
          whileHover={{ scale: 1.06 }}
          transition={{ type: 'spring', stiffness: 320, damping: 18 }}
          onClick={() => startCompose({ kind: 'note' })}
          className="home__fab home__fab--note"
          aria-label="leave a note"
        >
          <PaperFoldIcon />
        </motion.button>

        <motion.button
          ref={placeFabRef}
          whileTap={{ scale: 0.94 }}
          whileHover={{ scale: 1.04 }}
          transition={{ type: 'spring', stiffness: 320, damping: 20 }}
          onClick={() => startCompose()}
          className="home__fab home__fab--place"
          aria-label="add a place"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </motion.button>
      </div>

      <AnimatePresence>
        {selected && <PinSheet key={selected.id} pin={selected} />}
        {composing === 'place' && <ComposeSheet key="compose-place" />}
        {composing === 'note'  && <ComposeNote  key="compose-note" />}
      </AnimatePresence>

      <HistorySheet />
      <BucketSheet />


      <style>{homeCss}</style>
    </main>
  );
}

function CountBadge({ count }: { count: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="home__count"
    >
      <span className="home__count-num">{count}</span>
      <span className="home__count-label">place{count === 1 ? '' : 's'}</span>
    </motion.div>
  );
}

function BucketPill({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="home__wishes"
      aria-label={`open wishes (${count})`}
    >
      <WishGlyph />
      <span className="home__wishes-num">{count}</span>
      <span className="home__wishes-label">wish{count === 1 ? '' : 'es'}</span>
    </motion.button>
  );
}

function WishGlyph() {
  // Open envelope with a small spark — "places we mean to go"
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 7 L21 7 L21 18 L3 18 Z" />
      <path d="M3 7 L12 13 L21 7" opacity="0.65" />
      <circle cx="20" cy="5" r="1" fill="currentColor" />
    </svg>
  );
}

function KeptPill({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="home__kept"
      aria-label={`open history (${count} kept)`}
    >
      <StackedNotesIcon />
      <span className="home__kept-num">{count}</span>
      <span className="home__kept-label">kept</span>
    </motion.button>
  );
}

function HelpPill({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      className="home__help"
      aria-label="show keyboard shortcuts"
    >
      <span aria-hidden>?</span>
    </motion.button>
  );
}

function StackedNotesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6" y="3" width="12" height="14" rx="1.5" />
      <rect x="3" y="6" width="12" height="14" rx="1.5" opacity="0.55" />
    </svg>
  );
}

function PaperFoldIcon() {
  // A folded note. Triangle flap, body lines. Strokewidth 1.6 to feel like
  // a fountain pen, not a marker.
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M16 4v3h3" />
      <path d="M8 12h8M8 16h5" opacity="0.7" />
    </svg>
  );
}

const homeCss = `
.home {
  position: fixed; inset: 0;
  background: var(--c-cream);
}
.home__header {
  position: absolute; top: 0; left: 0; right: 0; z-index: 5;
  display: flex; justify-content: space-between; align-items: center;
  padding: calc(var(--safe-top) + var(--space-4)) var(--space-5) var(--space-3);
  pointer-events: none;
}
.home__brand {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255, 250, 245, 0.85);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  padding: 8px 14px 8px 10px;
  border-radius: var(--r-pill);
  /* Liquid-glass: 1px inner highlight + tinted shadow */
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    var(--shadow-sm);
  pointer-events: auto;
}
.home__brand-text {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1rem;
  color: var(--c-ink);
}
/* (sync indicator now handled by NetStatus, which also covers offline state) */

/* Wishes pill — same liquid-glass material as the others, mint-tinted icon
   so it reads as the "future / outward-facing" surface vs kept's rose. */
.home__wishes {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255, 250, 245, 0.85);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  padding: 8px 12px 8px 10px;
  border-radius: var(--r-pill);
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    var(--shadow-sm);
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}
.home__wishes svg { color: #78A890; transform: rotate(2deg); }   /* mint-leaning */
.home__wishes-num {
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--c-ink);
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.home__wishes-label {
  font-size: var(--t-caption);
  color: var(--c-ink-soft);
  text-transform: lowercase;
  letter-spacing: 0.06em;
}
.home__pills {
  display: inline-flex; align-items: center; gap: 8px;
  pointer-events: auto;
}
.home__count {
  display: inline-flex; align-items: baseline; gap: 6px;
  background: rgba(255, 250, 245, 0.85);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  padding: 8px 14px;
  border-radius: var(--r-pill);
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    var(--shadow-sm);
  pointer-events: auto;
}
.home__count-num {
  font-family: var(--font-display);
  font-size: 1.15rem;
  color: var(--c-rose-deep);
  font-weight: 500;
}
.home__count-label {
  font-size: var(--t-caption);
  color: var(--c-ink-soft);
  text-transform: lowercase;
  letter-spacing: 0.06em;
}

/* Kept pill — same liquid-glass material as the others, but the icon's
   slight rotation gives it a separate visual identity at a glance. */
.home__kept {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255, 250, 245, 0.85);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  padding: 8px 12px 8px 10px;
  border-radius: var(--r-pill);
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    var(--shadow-sm);
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}
.home__kept svg { color: var(--c-rose-deep); transform: rotate(-3deg); }
.home__kept-num {
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--c-ink);
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.home__kept-label {
  font-size: var(--t-caption);
  color: var(--c-ink-soft);
  text-transform: lowercase;
  letter-spacing: 0.06em;
}

/* Tertiary help affordance — small italic question mark in a circle.
   Same liquid-glass material as the other pills but compact. */
.home__help {
  width: 32px; height: 32px;
  display: grid; place-items: center;
  background: rgba(255, 250, 245, 0.85);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    var(--shadow-sm);
  cursor: pointer;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 0.95rem;
  color: var(--c-ink-soft);
  -webkit-tap-highlight-color: transparent;
  transition: color 0.22s var(--ease-soft);
}
.home__help:hover { color: var(--c-rose-deep); }

/* FAB stack — note above place, slight x-offset so they don't read as a
   single column. DESIGN_VARIANCE 8: stagger feels hand-arranged. */
.home__fabstack {
  position: absolute;
  bottom: calc(var(--safe-bottom) + var(--space-6));
  right: var(--space-5);
  z-index: 5;
  display: flex; flex-direction: column;
  align-items: flex-end;
  gap: 14px;
}
.home__fab {
  border: none; border-radius: var(--r-pill);
  display: grid; place-items: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  /* Tinted shadow, never blue, never neon */
  box-shadow:
    0 12px 32px -10px rgba(196,150,164,0.45),
    inset 0 1px 0 rgba(255,255,255,0.45);
}
.home__fab::before {
  content: ""; position: absolute; inset: -8px;
  border-radius: inherit;
  z-index: -1;
  filter: blur(10px);
  opacity: 0.4;
}
.home__fab--place {
  width: 56px; height: 56px;
  background: linear-gradient(135deg, var(--c-rose) 0%, var(--c-rose-deep) 100%);
  color: white;
}
.home__fab--place::before { background: var(--c-blush); }

.home__fab--note {
  width: 44px; height: 44px;
  background: linear-gradient(135deg, var(--c-paper) 0%, var(--c-peach) 100%);
  color: var(--c-ink);
  margin-right: 6px;     /* tiny x offset for asymmetry */
  border: 1px solid rgba(255,255,255,0.55);
}
.home__fab--note::before { background: var(--c-peach); }
`;
